import { getMillisecondsUntilExpiration } from '@/utils/date';
import { useEffect, useMemo, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';

const MS_PER_MINUTE = 60000;

const resolveRemaining = (expiresAt?: string | null): number | null => {
  if (!expiresAt) {
    return null;
  }
  const remaining = getMillisecondsUntilExpiration(expiresAt);

  // A malformed date yields NaN, which would render as "NaNh NaNm" and keep the
  // ticker alive forever, so treat it the same as nothing to count down to.
  return Number.isFinite(remaining) && remaining > 0 ? remaining : null;
};

/**
 * Milliseconds left until `expiresAt`, refreshed on every minute boundary while
 * the screen is focused. Returns null when there is nothing left to count down
 * to: no date, a malformed one, or a deadline that has already passed.
 */
export const useExpirationCountdown = (
  expiresAt?: string | null,
): number | null => {
  const [minuteCounter, setMinuteCounter] = useState(0);
  const isFocused = useIsFocused();

  // Bump the counter on focus return so the value recomputes; the time elapsed
  // while unfocused is covered because getMillisecondsUntilExpiration reads the
  // current clock.
  useEffect(() => {
    if (isFocused) {
      setMinuteCounter((count) => count + 1);
    }
  }, [isFocused]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    // Re-align on every tick so the displayed minute never lags behind the real
    // one, and stop scheduling once the deadline has passed.
    const scheduleNextTick = () => {
      const remaining = resolveRemaining(expiresAt);
      if (remaining === null) {
        return;
      }

      timeoutId = setTimeout(
        () => {
          setMinuteCounter((count) => count + 1);
          scheduleNextTick();
        },
        remaining % MS_PER_MINUTE || MS_PER_MINUTE,
      );
    };

    scheduleNextTick();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [expiresAt, isFocused]);

  return useMemo(() => {
    return resolveRemaining(expiresAt);

    // minuteCounter is the tick that forces the recompute every minute.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiresAt, minuteCounter]);
};
